import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { catchError, delay, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import {
    merge,
    Observable,
    of,
    shareReplay,
    combineLatest,
    timer,
    Subject,
    withLatestFrom,
    BehaviorSubject,
    from,
} from 'rxjs';
import { BazisModalService } from '@bazis/shared/services/modal.service';
import { BazisCryptoService } from '@bazis/signature/crypto/crypto.service';
import { EntData, EntDocumentSettings, ItemToSign } from '@bazis/shared/models/srv.types';
import { BazisDocumentService } from '@bazis/shared/services/document.service';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { BazisMediaQueryService } from '@bazis/shared/services/media-query.service';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { BazisDocusignService } from '@bazis/signature/docusign/docusign.service';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { BazisAlertService } from '@bazis/shared/services/alert.service';
import { CommonModule, NgClass } from '@angular/common';
import { ButtonModule } from '@bazis/shared/components/web/button/button.module';
import { IconModule } from '@bazis/shared/components/web/icon/icon.module';
import { ToolbarModule } from '@bazis/shared/components/web/toolbar/toolbar.module';
import { FileInlineComponent } from '@bazis/shared/components/file/file-inline.component';
import { DefaultLoaderComponent } from '@bazis/shared/components/loaders/default-loader.component';
import { DocumentAccordeonComponent } from '@bazis/shared/components/document-accordeon/document-accordeon.component';
import { TranslocoModule } from '@jsverse/transloco';
import { ColorDirective } from '@bazis/shared/directives/color.directive';
import { FormGroup } from '@angular/forms';
import { BazisNCALayerService } from '@bazis/signature/ncalayer/ncalayer.service';

@Component({
    selector: 'bazis-ncalayer-signing',
    templateUrl: './signing.component.html',
    imports: [
        ButtonModule,
        IconModule,
        ToolbarModule,
        CommonModule,
        FileInlineComponent,
        DefaultLoaderComponent,
        DocumentAccordeonComponent,
        ColorDirective,
        TranslocoModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NCALayerSigningComponent implements OnInit {
    constructor(
        private crypto: BazisCryptoService,
        private modalService: BazisModalService,
        private documentService: BazisDocumentService,
        public mqSrv: BazisMediaQueryService,
        public srvService: BazisSrvService,
        public authService: BazisAuthService,
        public NCALayerService: BazisNCALayerService,
        public entityService: BazisEntityService,
        public alertService: BazisAlertService,
    ) {}

    @Input() itemsToSign: TemplateObservable<EntDocumentSettings[]>;

    @Input() forceLoadEntitiesAfterSigning: boolean;

    itemsToSign$: Observable<ItemToSign[]>;

    allItems = [];

    signed = {};

    step$ = new BehaviorSubject(0);

    filesIsUploaded = new TemplateObservable(0);

    startSigningEntity$ = new BehaviorSubject(true);

    signingDoc$ = this.startSigningEntity$.pipe(
        mergeMap(() => this.sign$()),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    ngOnInit() {
        this.itemsToSign$ = this.itemsToSign.$.pipe(
            tap((itemsToSign) => {
                this.allItems = itemsToSign;
            }),
            map((itemsToSign) => {
                if (!itemsToSign) return [];
                return itemsToSign.map((item) => {
                    const documentSource = item.document
                        ? of({ ...item.document }).pipe(shareReplay(SHARE_REPLAY_SETTINGS))
                        : this.documentService
                              .getSignDocument$(
                                  item.entityType,
                                  item.entityId,
                                  item.contextLabel,
                                  item.documentPayload,
                                  item.cancelPreviousSignings,
                              )
                              .pipe(shareReplay(SHARE_REPLAY_SETTINGS));
                    return {
                        ...item,
                        document$: documentSource.pipe(
                            take(1),
                            delay(1),
                            tap((v) => {
                                this.filesIsUploaded.set(this.filesIsUploaded._ + 1);
                            }),
                            shareReplay({ bufferSize: 1, refCount: false }),
                        ),
                    };
                });
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    sign$() {
        return this.itemsToSign$.pipe(
            switchMap((items) => combineLatest([...items.map((item) => item.document$)])),
            filter((documents) => documents.filter((v) => !!v).length === documents.length),
            mergeMap((documents) =>
                combineLatest([
                    ...documents.map((document) =>
                        this.documentService.getFileContentToSign$(
                            document.$snapshot.file_upload_url,
                        ),
                    ),
                ]),
            ),
            filter((files) => files.filter((v) => !!v).length === files.length),
            mergeMap((files) => this.NCALayerService.signDocuments$(files)),
            withLatestFrom(this.itemsToSign$),
            mergeMap(([signatures, itemsToSign]) =>
                this.documentService.signEntityDocuments$(
                    signatures.map((signature, index) => {
                        return {
                            settings: {
                                entityType: itemsToSign[index].entityType,
                                entityId: itemsToSign[index].entityId,
                                contextLabel: itemsToSign[index].contextLabel,
                                withoutIncludedDocuments:
                                    itemsToSign[index].withoutIncludedDocuments,
                            },
                            signature,
                            signBodyPayload: itemsToSign[index].signBodyPayload || null,
                        };
                    }),
                    this.forceLoadEntitiesAfterSigning,
                ),
            ),
            withLatestFrom(this.itemsToSign$),
            tap(([result, itemsToSign]) => {
                itemsToSign.forEach((itemToSign) => {
                    this.signed[
                        `${itemToSign.entityType}-${itemToSign.entityId}-${
                            itemToSign.contextLabel || ''
                        }`
                    ] = true;
                });

                this.openAlert();
                this.close();
            }),
            catchError((err) => {
                this.step$.next(0);
                console.warn('signature error [!]', err);
                return of(err);
            }),
        );
    }

    close() {
        this.modalService.dismiss({ signed: this.signed });
    }

    startSigning() {
        this.step$.next(1);
    }

    openAlert() {
        const headerKey = this.allItems.length > 1 ? 'crypto.docsSigned' : 'crypto.docSigned';
        const messageKey =
            this.allItems.length === 1 ? 'crypto.successDocSigned1' : 'crypto.successDocsSignedAll';

        const alert = this.alertService.create({
            icon: 'check',
            color: 'success',
            headerKey,
            messageKey,
            //messageParams,
            buttons: [
                {
                    titleKey: 'crypto.close',
                    handler: () => {
                        this.close();
                    },
                },
            ],
        });

        alert.onDidDismiss().then(() => {});
    }
}
